import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import { UnreadBadge } from '../teamChat/UnreadBadge';

export type HealthDot = 'green' | 'yellow' | 'red' | 'gray';

const DOT_STYLES: Record<HealthDot, string> = {
  green: 'bg-green-500',
  yellow: 'bg-amber-400',
  red: 'bg-red-500',
  // Neutral dot used for people/team members (no health semantics).
  gray: 'bg-muted-foreground',
};

interface SidebarNavItemProps {
  to: string;
  label: string;
  icon?: LucideIcon | ComponentType<{ size?: number; className?: string }>;
  // Render as a nested sub-item (extra indentation, smaller text).
  nested?: boolean;
  // Optional health indicator dot shown before the label.
  dot?: HealthDot;
  // react-router NavLink "end" prop to avoid matching parent routes for index links.
  end?: boolean;
  // Optional count pill after the label (e.g. number of projects).
  badge?: number;
  // 'count' (default) is the muted grey pill used for totals; 'unread' is the
  // red circle used on team-chat conversations, for "something new for you".
  badgeTone?: 'count' | 'unread';
  // Called after navigation (used to close the mobile overlay).
  onNavigate?: () => void;
}

export function SidebarNavItem({
  to,
  label,
  icon: Icon,
  nested = false,
  dot,
  end,
  badge,
  badgeTone = 'count',
  onNavigate,
}: SidebarNavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          'group relative flex items-center gap-3 rounded-md text-sm font-bold transition-colors',
          // `min-h-10` rather than padding alone: a top-level row must be the
          // same height as a SidebarSection header even though that one carries
          // a chevron and this one may carry nothing but a label.
          nested ? 'py-1.5 pl-9 pr-3' : 'min-h-10 px-3 py-2',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {/* Active left border indicator. */}
          <span
            aria-hidden
            className={[
              'absolute left-0 h-6 w-1 rounded-r-full bg-foreground transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
          />
          {Icon && (
            <Icon
              size={18}
              className={
                isActive
                  ? 'text-sidebar-accent-foreground'
                  : 'text-muted-foreground group-hover:text-foreground'
              }
            />
          )}
          {dot && (
            <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${DOT_STYLES[dot]}`} />
          )}
          <span className="flex-1 truncate">{label}</span>
          {badge !== undefined && badge > 0 && badgeTone === 'unread' && (
            <UnreadBadge count={badge} />
          )}
          {badge !== undefined && badge > 0 && badgeTone === 'count' && (
            <span className="shrink-0 rounded-full bg-sidebar-accent px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
